/* ==========================================================================
   The SoArt — Select add-ons (front-end controller)
   product-addons.js
   --------------------------------------------------------------------------
   A <product-addons> custom element that:

     1. Tracks a per-add-on quantity via a compact − N + stepper (real buttons
        with descriptive aria-labels; the count uses aria-live). Quantity 0 =
        unselected; the range is clamped to [0, max]. Keyboard operable.

     2. Adds the selected add-ons to the cart as SEPARATE Shopify line items
        AFTER the main flower product is added — by listening for Horizon's own
        `cart:update` event (dispatched by ProductFormComponent once the main
        product's native AJAX add succeeds). It never replaces or duplicates
        the product form, and it works identically whether the shopper used the
        normal Add-to-Cart button, the SoArt sticky bar, or the delivery modal
        (all three route through the same native add).

     3. Refreshes the cart drawer through Horizon's existing architecture:
        it posts the add-ons in ONE /cart/add.js request (items[] array — no
        per-item race), asking for the same cart `sections` Horizon uses, then
        dispatches ONE `cart:update` event carrying those sections so
        cart-items-component, cart-icon and the drawer all update via their own
        listeners.

   Why this is safe with auto-open ON:
     • The drawer auto-opens on the MAIN add (source: 'product-form-component').
     • Our follow-up event uses a DIFFERENT source ('soart-addons') and the
       ABSOLUTE cart item_count from the /cart/add.js response, so the bubble is
       SET to the true total, never double-incremented.
     • Horizon's DialogComponent.showDialog() early-returns when the dialog is
       already open, so the follow-up event cannot trigger a second open
       animation.

   No dependency on the SoArt modules or Horizon internals beyond the public
   `cart:update` event name and the standard Shopify /cart/add.js contract.
   ========================================================================== */

(function () {
  'use strict';

  var CART_UPDATE_EVENT = 'cart:update'; // Horizon: ThemeEvents.cartUpdate
  var MAIN_ADD_SOURCE = 'product-form-component';
  var OUR_SOURCE = 'soart-addons';

  /* Resolve the cart-add / cart URLs defensively (theme root may vary). */
  function routes() {
    var t = (window.Theme && window.Theme.routes) || {};
    var root = t.root_url || window.routes && window.routes.root_url || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return {
      add: t.cart_add_url || root + 'cart/add',
      base: root
    };
  }

  /* Collect the section ids Horizon renders for the cart (drawer + any cart
     items components on the page). Mirrors product-form.js / cart-items. */
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function ProductAddons() {
    return Reflect.construct(HTMLElement, [], ProductAddons);
  }

  ProductAddons.prototype = Object.create(HTMLElement.prototype);
  ProductAddons.prototype.constructor = ProductAddons;

  ProductAddons.prototype.connectedCallback = function () {
    if (this._init) return;
    this._init = true;

    this._busy = false; // mutex: guards against double add-on submits
    this._track = this.querySelector('[data-soart-addons-track]');
    this._status = this.querySelector('[data-soart-addons-status]');
    this._cards = Array.prototype.slice.call(
      this.querySelectorAll('[data-soart-addon]')
    );

    this._bindSteppers();
    this._bindNav();
    this._bindCartUpdate();
    this._bindPopovers();
    this._syncOverflow();

    /* Re-evaluate arrow visibility on resize (debounced via rAF). */
    var self = this;
    this._onResize = function () {
      if (self._raf) return;
      self._raf = requestAnimationFrame(function () {
        self._raf = null;
        self._syncOverflow();
        self._closePopover(); // reposition would be stale after a resize
      });
    };
    window.addEventListener('resize', this._onResize);
  };

  ProductAddons.prototype.disconnectedCallback = function () {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onCartUpdate) {
      document.removeEventListener(CART_UPDATE_EVENT, this._onCartUpdate);
    }
    if (this._onDocPointer) {
      document.removeEventListener('pointerdown', this._onDocPointer, true);
    }
    if (this._onDocKey) {
      document.removeEventListener('keydown', this._onDocKey, true);
    }
    if (this._onAnyScroll) {
      document.removeEventListener('scroll', this._onAnyScroll, true);
    }
  };

  /* ---- Quantity steppers ---------------------------------------------- */
  /* Each card owns its quantity as a data-qty attribute (the single source of
     truth). The − / + buttons mutate it; _selectedItems() reads it at add time.
     Quantity 0 = unselected. Range is clamped to [0, data-max-qty]. */
  ProductAddons.prototype._bindSteppers = function () {
    var self = this;

    this._cards.forEach(function (card) {
      var soldout = card.getAttribute('data-soldout') === 'true';
      var minus = card.querySelector('[data-soart-addon-minus]');
      var plus = card.querySelector('[data-soart-addon-plus]');

      /* Sold-out products stay fully disabled (both buttons already disabled in
         Liquid); nothing to wire. */
      if (soldout) return;

      if (minus) {
        minus.addEventListener('click', function () {
          self._changeQty(card, -1);
        });
      }
      if (plus) {
        plus.addEventListener('click', function () {
          self._changeQty(card, 1);
        });
      }

      /* Initialise disabled states for quantity 0. */
      self._syncCardQtyUI(card);
    });

    /* Multi-variant cards: when the shopper changes the compact selector,
       swap the displayed price to that variant's price. All values are
       PRE-FORMATTED by Liquid (data-price / data-compare-at) — the JS does no
       currency math, it only shows the right pre-formatted string. Quantity is
       untouched; the variant id is still read fresh at add time. */
    this._selects = Array.prototype.slice.call(
      this.querySelectorAll('[data-soart-addon-variant]')
    );
    this._selects.forEach(function (select) {
      select.addEventListener('change', function () {
        self._updateCardPrice(select);
        self._updateCardImage(select);

        /* Keep the card's raw integer price in sync with the chosen variant so
           the Add-to-Cart total controller reads the correct unit price. */
        var card = select.closest('.soart-addons__card');
        if (card) {
          var opt = select.options[select.selectedIndex];
          card.setAttribute(
            'data-addon-price',
            (opt && opt.getAttribute('data-price-cents')) || '0'
          );

          self.dispatchEvent(
            new CustomEvent('soart:addons:changed', {
              bubbles: true,
              detail: {
                card: card,
                quantity: self._getQty(card)
              }
            })
          );
        }
      });
    });
  };

  /* Read a card's current quantity (integer ≥ 0). */
  ProductAddons.prototype._getQty = function (card) {
    var n = parseInt(card.getAttribute('data-qty'), 10);
    return isNaN(n) || n < 0 ? 0 : n;
  };

  /* Read a card's maximum quantity (defaults to 10 if unset/invalid). */
  ProductAddons.prototype._getMaxQty = function (card) {
    var n = parseInt(card.getAttribute('data-max-qty'), 10);
    return isNaN(n) || n < 1 ? 10 : n;
  };

  /* Change a card's quantity by delta, clamped to [0, max], then update UI. */
  ProductAddons.prototype._changeQty = function (card, delta) {
    if (card.getAttribute('data-soldout') === 'true') return;

    var max = this._getMaxQty(card);
    var next = this._getQty(card) + delta;
    if (next < 0) next = 0;
    if (next > max) next = max;

    card.setAttribute('data-qty', String(next));
    this._syncCardQtyUI(card);
    this._clearStatus();

    /* Notify the Add-to-Cart total controller (and any other listener) that an
       add-on's quantity changed. Event-driven: no polling, no DOM observing. */
    this.dispatchEvent(
      new CustomEvent('soart:addons:changed', {
        bubbles: true,
        detail: {
          card: card,
          quantity: next
        }
      })
    );
  };

  /* Reflect a card's quantity in its stepper value, badge, and disabled states.
     Does NOT touch cart logic — purely visual/state sync. */
  ProductAddons.prototype._syncCardQtyUI = function (card) {
    var qty = this._getQty(card);
    var max = this._getMaxQty(card);
    var soldout = card.getAttribute('data-soldout') === 'true';

    var valueEl = card.querySelector('[data-soart-addon-qty-value]');
    var minus = card.querySelector('[data-soart-addon-minus]');
    var plus = card.querySelector('[data-soart-addon-plus]');
    var badge = card.querySelector('[data-soart-addon-badge]');
    var badgeCount = card.querySelector('[data-soart-addon-badge-count]');

    if (valueEl) valueEl.textContent = String(qty);

    /* − disabled at 0; + disabled at max; both disabled when sold out. */
    if (minus) minus.disabled = soldout || qty <= 0;
    if (plus) plus.disabled = soldout || qty >= max;

    /* Live count badge over the image, shown only when qty > 0. */
    if (badge) {
      if (qty > 0) {
        if (badgeCount) badgeCount.textContent = String(qty);
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }
  };

  /* Update a card's displayed price from the selected <option>'s pre-formatted
     data-price / data-compare-at (both produced by Liquid's `money` filter). */
  ProductAddons.prototype._updateCardPrice = function (select) {
    var card = select.closest('.soart-addons__card');
    if (!card) return;

    var opt = select.options[select.selectedIndex];
    if (!opt) return;

    var currentEl = card.querySelector('[data-soart-addon-price-current]');
    var compareEl = card.querySelector('[data-soart-addon-price-compare]');

    var price = opt.getAttribute('data-price') || '';
    var compare = opt.getAttribute('data-compare-at') || '';

    if (currentEl && price) currentEl.textContent = price;

    if (compareEl) {
      if (compare) {
        compareEl.textContent = compare;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }
  };

  /* Swap the card image to the selected variant's pre-rendered image. All
     values (src, srcset, alt) are produced by Liquid on each <option>; the JS
     makes NO storefront API request. If the chosen variant has no image of its
     own, we fall back to the product's featured image stored on the media span
     — never leaving a previous variant's picture showing incorrectly. */
  ProductAddons.prototype._updateCardImage = function (select) {
    var card = select.closest('.soart-addons__card');
    if (!card) return;

    var img = card.querySelector('.soart-addons__image');
    if (!img) return; // placeholder-only card (product had no image at all)

    var media = card.querySelector('.soart-addons__media');
    var opt = select.options[select.selectedIndex];
    if (!opt) return;

    var src = opt.getAttribute('data-image') || '';
    var srcset = opt.getAttribute('data-image-srcset') || '';
    var alt = opt.getAttribute('data-image-alt') || '';

    /* Variant has no image → fall back to the product featured image. This
       also covers variants that share an image: the shared src is identical,
       so the swap is a no-op paint and the correct picture stays shown. */
    if (!src && media) {
      src = media.getAttribute('data-soart-addon-fallback-src') || '';
      srcset = '';
      alt = media.getAttribute('data-soart-addon-fallback-alt') || alt;
    }

    if (!src) return; // nothing usable; leave current image untouched

    /* srcset must be cleared before (or alongside) src so the browser doesn't
       keep resolving the old responsive candidates against the new src. */
    if (srcset) {
      img.setAttribute('srcset', srcset);
    } else {
      img.removeAttribute('srcset');
    }
    img.setAttribute('src', src);
    if (alt !== '') img.setAttribute('alt', alt);
  };

  /* Returns [{ id: variantId, quantity: n }, …] for every add-on whose
     quantity is > 0. Reads the per-card data-qty (source of truth) and the
     variant id (from the compact selector, or the fixed hidden input). */
  ProductAddons.prototype._selectedItems = function () {
    var self = this;
    var items = [];
    this._cards.forEach(function (card) {
      if (card.getAttribute('data-soldout') === 'true') return;

      var qty = self._getQty(card);
      if (qty <= 0) return; // only include selected (quantity > 0) add-ons

      var variantId = null;
      var select = card.querySelector('[data-soart-addon-variant]');
      if (select) {
        var opt = select.options[select.selectedIndex];
        if (!opt || opt.disabled) return; // guard: never add a sold-out variant
        variantId = select.value;
      } else {
        var fixed = card.querySelector('[data-soart-addon-variant-fixed]');
        variantId = fixed ? fixed.value : null;
      }

      if (variantId) items.push({ id: Number(variantId), quantity: qty });
    });
    return items;
  };

  /* ---- React to the main product being added --------------------------- */
  ProductAddons.prototype._bindCartUpdate = function () {
    var self = this;
    this._onCartUpdate = function (event) {
      var detail = event && event.detail;
      var data = detail && detail.data;
      if (!data) return;

      /* Only react to the MAIN product's native add — not to our own follow-up
         event, not to cart-page quantity changes, not to add-on adds. */
      if (data.source !== MAIN_ADD_SOURCE) return;
      if (data.didError) return; // main add failed → do nothing
      if (self._busy) return; // an add-on batch is already in flight

      var items = self._selectedItems();
      if (!items.length) return; // no add-ons selected → stock behaviour, untouched

      self._addSelected(items);
    };
    document.addEventListener(CART_UPDATE_EVENT, this._onCartUpdate);
  };

  /* ---- Add the selected add-ons in ONE request, then refresh ----------- */
  ProductAddons.prototype._addSelected = function (items) {
    var self = this;
    this._busy = true;

    var r = routes();
    var sections = cartSectionIds();

    var body = { items: items };
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    fetch(r.add, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          return { ok: res.ok, json: json };
        });
      })
      .then(function (result) {
        if (!result.ok || (result.json && result.json.status)) {
          /* Shopify returns { status, message, description } on failure.
             The main product is already safely in the cart; surface a quiet
             notice and leave the cart as-is. */
          var msg =
            (result.json && (result.json.description || result.json.message)) ||
            'Some add-ons could not be added.';
          self._setStatus(msg, true);
          return;
        }

        /* Success: refresh the drawer via Horizon's own event pipeline.
           We fetch the authoritative item_count so the bubble is SET, not
           incremented. Prefer sections from this response; if absent, fetch
           them so the drawer still morphs to the final state. */
        self._refreshCart(result.json);
        self._resetSelection();
      })
      .catch(function () {
        self._setStatus(
          'We couldn’t add your add-ons just now. Your main item is in the cart — please try adding extras again.',
          true
        );
      })
      .finally(function () {
        self._busy = false;
      });
  };

  /* Dispatch Horizon's cart:update so drawer + bubble + totals refresh.
     Uses a NON product-form source and the ABSOLUTE item_count → the bubble is
     set to the true total (never double-counted), and the already-open drawer
     simply morphs (showDialog() is a no-op while open → no second animation). */
  ProductAddons.prototype._refreshCart = function (addResponse) {
    var self = this;

    function dispatchWith(sections, itemCount) {
      var evt;
      try {
        /* Native CustomEvent keeps us decoupled from Horizon's ESM bundle. */
        evt = new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: OUR_SOURCE,
            data: {
              source: OUR_SOURCE,
              itemCount: typeof itemCount === 'number' ? itemCount : 0,
              sections: sections || {}
            }
          }
        });
      } catch (e) {
        return;
      }
      document.dispatchEvent(evt);
    }

    /* /cart/add.js with `sections` returns { …, sections: {id: html}, ... }
       but NOT a whole-cart item_count. Get the true total from /cart.js. */
    var sectionsFromAdd =
      addResponse && addResponse.sections ? addResponse.sections : null;

    fetch(routes().base + 'cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;

        if (sectionsFromAdd) {
          dispatchWith(sectionsFromAdd, count);
        } else {
          /* No sections came back (e.g. no cart-items-component on page) —
             ask Horizon to re-render by dispatching with empty sections; its
             listeners will re-fetch the section themselves. */
          dispatchWith({}, count);
        }
      })
      .catch(function () {
        /* Even if the count fetch fails, still refresh sections so the drawer
           shows the right items; fall back to a safe absolute of 0 count only
           for the bubble math (sections carry the real content). */
        if (sectionsFromAdd) dispatchWith(sectionsFromAdd, 0);
      });
  };

  /* ---- Reset quantities after a successful add ------------------------- */
  ProductAddons.prototype._resetSelection = function () {
    var self = this;
    this._cards.forEach(function (card) {
      card.setAttribute('data-qty', '0');
      self._syncCardQtyUI(card); // resets value, badge, and −/+ disabled states
    });

    /* One event so the Add-to-Cart total returns to the main-product-only total
       after a successful add. */
    this.dispatchEvent(
      new CustomEvent('soart:addons:changed', {
        bubbles: true,
        detail: {
          card: null,
          quantity: 0
        }
      })
    );
  };

  /* ---- Status helpers -------------------------------------------------- */
  ProductAddons.prototype._setStatus = function (text, isError) {
    if (!this._status) return;
    this._status.textContent = text || '';
    this._status.classList.toggle('is-error', !!isError);
  };

  ProductAddons.prototype._clearStatus = function () {
    this._setStatus('', false);
  };

  /* ====================================================================== *
   *  Info popovers                                                          *
   *  Self-contained: touches NO cart logic and NO selection state. The ⓘ    *
   *  trigger is a sibling of the toggle, so opening a description never      *
   *  selects/deselects the add-on.                                          *
   * ====================================================================== */
  ProductAddons.prototype._bindPopovers = function () {
    var self = this;

    this._infoBtns = Array.prototype.slice.call(
      this.querySelectorAll('[data-soart-addon-info]')
    );
    if (!this._infoBtns.length) return;

    this._openInfo = null; // the currently-open trigger button (only one)

    this._infoBtns.forEach(function (btn) {
      var wrap = btn.closest('[data-soart-addon-info-wrap]');
      var pop = wrap ? wrap.querySelector('[data-soart-addon-popover]') : null;
      if (!pop) return;

      /* Desktop: hover + keyboard focus open; leaving/blur closes. These are
         harmless on touch (where the click/tap handler drives behaviour). */
      btn.addEventListener('mouseenter', function () {
        if (self._isCoarsePointer()) return;
        self._openPopover(btn, pop);
      });
      wrap.addEventListener('mouseleave', function () {
        if (self._isCoarsePointer()) return;
        if (self._openInfo === btn) self._closePopover();
      });
      btn.addEventListener('focus', function () {
        self._openPopover(btn, pop);
      });
      btn.addEventListener('blur', function () {
        /* Close on blur unless focus moved into the popover itself. */
        if (self._openInfo === btn) self._closePopover();
      });

      /* Mobile/tap + click: toggle. Tapping again closes. */
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (self._openInfo === btn) {
          self._closePopover();
        } else {
          self._openPopover(btn, pop);
        }
      });
    });

    /* One open at a time + outside tap closes. Capture phase so it runs before
       other handlers, but we never touch the toggle's own click. */
    this._onDocPointer = function (event) {
      if (!self._openInfo) return;
      var wrap = self._openInfo.closest('[data-soart-addon-info-wrap]');
      var pop = self._currentPop;
      if (wrap && wrap.contains(event.target)) return;
      if (pop && pop.contains(event.target)) return;
      self._closePopover();
    };
    document.addEventListener('pointerdown', this._onDocPointer, true);

    /* Escape closes and returns focus to the trigger. */
    this._onDocKey = function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!self._openInfo) return;
      var btn = self._openInfo;
      self._closePopover();
      if (btn && typeof btn.focus === 'function') btn.focus();
    };
    document.addEventListener('keydown', this._onDocKey, true);

    /* Close on ANY scroll — the carousel track, a scrollable ancestor, or the
       page/window — because a fixed-positioned popover would otherwise drift
       out of alignment with its icon. A capture-phase listener on document sees
       scroll events from every scroll container (scroll doesn't bubble, but it
       does propagate in the capture phase). Passive = never blocks scrolling. */
    this._onAnyScroll = function (event) {
      if (!self._openInfo) return;
      /* Ignore scrolling that happens INSIDE the open popover (long
         descriptions scroll internally); close for every other scroll. */
      var pop = self._currentPop;
      if (pop && event.target && pop.contains(event.target)) return;
      self._closePopover();
    };
    document.addEventListener('scroll', this._onAnyScroll, {
      capture: true,
      passive: true
    });
  };

  ProductAddons.prototype._isCoarsePointer = function () {
    return (
      window.matchMedia && window.matchMedia('(pointer: coarse)').matches
    );
  };

  /* Open a popover: move it to the root (to escape the scroller's clip),
     position it above the icon (or below if there's no room), then animate. */
  ProductAddons.prototype._openPopover = function (btn, pop) {
    if (this._openInfo === btn) return;
    this._closePopover(); // enforce single-open

    var card = btn.closest('.soart-addons__card');
    var cardRect = card ? card.getBoundingClientRect() : btn.getBoundingClientRect();
    var btnRect = btn.getBoundingClientRect();

    /* Park the popover on the root element so no ancestor overflow clips it. */
    if (pop.parentNode !== this) this.appendChild(pop);

    pop.hidden = false;
    pop.style.visibility = 'hidden';
    pop.classList.remove('is-below');

    /* Width ≈ card width (clamped by CSS max-width). */
    pop.style.width = Math.round(cardRect.width) + 'px';

    /* Measure, then decide above vs below. */
    var popRect = pop.getBoundingClientRect();
    var gap = 8;
    var spaceAbove = cardRect.top;
    var below = spaceAbove < popRect.height + gap;

    var top;
    if (below) {
      pop.classList.add('is-below');
      top = cardRect.bottom + gap;
    } else {
      top = cardRect.top - popRect.height - gap;
    }

    /* Right-align to the card, but keep within the viewport. */
    var left = cardRect.right - popRect.width;
    var minLeft = 8;
    var maxLeft = window.innerWidth - popRect.width - 8;
    if (left < minLeft) left = minLeft;
    if (left > maxLeft) left = Math.max(minLeft, maxLeft);

    pop.style.top = Math.round(top) + 'px';
    pop.style.left = Math.round(left) + 'px';
    pop.style.insetInlineEnd = 'auto';
    pop.style.insetBlockEnd = 'auto';
    pop.style.visibility = '';

    /* Trigger the fade/slide on the next frame. */
    requestAnimationFrame(function () {
      pop.classList.add('is-open');
    });

    btn.setAttribute('aria-expanded', 'true');
    this._openInfo = btn;
    this._currentPop = pop;
  };

  ProductAddons.prototype._closePopover = function () {
    var pop = this._currentPop;
    var btn = this._openInfo;
    this._openInfo = null;
    this._currentPop = null;
    if (btn) btn.setAttribute('aria-expanded', 'false');
    if (!pop) return;

    pop.classList.remove('is-open');

    /* After the transition, hide + return the node to its card so the DOM
       stays tidy and the next open re-measures cleanly. */
    var self = this;
    var owner = btn ? btn.closest('[data-soart-addon-info-wrap]') : null;
    var done = function () {
      pop.hidden = true;
      pop.classList.remove('is-below');
      pop.style.top = '';
      pop.style.left = '';
      pop.style.width = '';
      if (owner && pop.parentNode === self) owner.appendChild(pop);
    };
    /* Respect reduced motion (no transitionend fires when transition is none). */
    if (self._isReducedMotion()) {
      done();
    } else {
      var handled = false;
      var onEnd = function () {
        if (handled) return;
        handled = true;
        pop.removeEventListener('transitionend', onEnd);
        done();
      };
      pop.addEventListener('transitionend', onEnd);
      setTimeout(onEnd, 260); // fallback if transitionend is missed
    }
  };

  ProductAddons.prototype._isReducedMotion = function () {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  };

  /* ---- Carousel navigation (desktop arrows) ---------------------------- */
  ProductAddons.prototype._bindNav = function () {
    var self = this;
    var prev = this.querySelector('[data-soart-addons-prev]');
    var next = this.querySelector('[data-soart-addons-next]');
    if (!this._track) return;

    function page(dir) {
      var amount = self._track.clientWidth * 0.8 * dir;
      self._track.scrollBy({ left: amount, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { page(-1); });
    if (next) next.addEventListener('click', function () { page(1); });

    this._track.addEventListener('scroll', function () {
      if (self._scrollRaf) return;
      self._scrollRaf = requestAnimationFrame(function () {
        self._scrollRaf = null;
        self._syncArrows(prev, next);
      });
    });

    this._navPrev = prev;
    this._navNext = next;
  };

  /* Toggle .is-overflowing (CSS reveals arrows only when needed + desktop). */
  ProductAddons.prototype._syncOverflow = function () {
    if (!this._track) return;
    var overflowing = this._track.scrollWidth - this._track.clientWidth > 2;
    this.classList.toggle('is-overflowing', overflowing);
    this._syncArrows(this._navPrev, this._navNext);
  };

  /* Hide the prev arrow at the start and the next arrow at the end. */
  ProductAddons.prototype._syncArrows = function (prev, next) {
    if (!this._track) return;
    var max = this._track.scrollWidth - this._track.clientWidth;
    var x = this._track.scrollLeft;
    var overflowing = max > 2;
    if (prev) prev.hidden = !overflowing || x <= 2;
    if (next) next.hidden = !overflowing || x >= max - 2;
  };

  if (!customElements.get('product-addons')) {
    customElements.define('product-addons', ProductAddons);
  }
})();
